import React from 'react';
import { IconCheckmark } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';

import * as styles from './Toggle.styles';

export interface ToggleProps {
  active: boolean;
  boldText?: boolean;
  disabled?: boolean;
  href?: string;
  label: string;
  name: string;
  onToggle: (currentActive: boolean) => void;
  secondaryLabelText?: string;
  value: string;
}

export const Toggle = ({
  active,
  boldText,
  disabled,
  href,
  label: labelText,
  name,
  onToggle,
  secondaryLabelText,
  value,
}: ToggleProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onToggle(event.target.checked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
    if (disabled) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle(!active);
    }
  };

  const handleUnderlayLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const label = event.currentTarget.closest('label');
    const input = label?.querySelector<HTMLInputElement>('input[type="checkbox"]');
    input?.click();
  };

  const outerStyle = [styles.toggleItem, disabled && styles.disabled];
  const showLink = Boolean(href && !disabled);

  return (
    <span css={outerStyle}>
      <label
        aria-disabled={disabled}
        aria-pressed={active}
        css={styles.toggleLabelRoot}
        onKeyDown={handleKeyDown}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        {showLink && (
          <a
            aria-hidden="true"
            css={styles.crawlLinkUnderlay}
            data-testid={`${name}-${value}-crawl-link`}
            href={href}
            onClick={handleUnderlayLinkClick}
            rel="nofollow"
            tabIndex={-1}
          >
            <span css={visuallyHidden}>
              {labelText}
              {secondaryLabelText ? ` ${secondaryLabelText}` : ''}
            </span>
          </a>
        )}
        <span css={styles.toggleForeground}>
          <h4
            css={styles.toggleLabel}
            style={{ '--toggleFontWeight': boldText ? '600' : '400' } as styles.ToggleFontWeightCSS}
          >
            {labelText}
            {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
          </h4>
          <input
            checked={active}
            css={styles.hiddenInput}
            disabled={disabled}
            name={name}
            onChange={handleChange}
            type="checkbox"
            value={value}
          />

          {/* Visual toggle switch */}
          <div css={styles.toggleSwitch}>
            <span css={styles.toggleSlider}>
              {active && <IconCheckmark color="var(--neutral-black-base)" height={16} width={16} />}
            </span>
          </div>
        </span>
      </label>
    </span>
  );
};
